import { useState, useEffect } from 'react';
import type { ProductDTO } from '@horology/shared-types';
import { createProduct, updateProduct } from './products.api';
import { imageUrl } from '../../lib/format';
import { readApiError } from '../../lib/api';

interface Props {
  initial?: ProductDTO | null;
  onSaved: (p: ProductDTO) => void;
  onCancel: () => void;
}

export function ProductForm({ initial, onSaved, onCancel }: Props) {
  const [sku, setSku] = useState(initial?.sku ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [purchasePrice, setPurchasePrice] = useState(String(initial?.purchasePrice ?? '0'));
  const [taxRate, setTaxRate] = useState(String(initial?.taxRate ?? '19'));
  const [salePrice, setSalePrice] = useState(String(initial?.salePrice ?? '0'));
  const [isService, setIsService] = useState(initial?.isService ?? false);
  const [isFavorite, setIsFavorite] = useState(initial?.isFavorite ?? false);
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | undefined>(imageUrl(initial?.imagePath));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const fd = new FormData();
      fd.set('sku', sku);
      fd.set('name', name);
      fd.set('description', description ?? '');
      fd.set('purchasePrice', purchasePrice);
      fd.set('taxRate', taxRate);
      fd.set('salePrice', salePrice);
      fd.set('isService', String(isService));
      fd.set('isFavorite', String(isFavorite));
      fd.set('isActive', String(isActive));
      if (file) fd.set('image', file);

      const result = initial ? await updateProduct(initial.id, fd) : await createProduct(fd);
      onSaved(result);
    } catch (err) {
      setError(readApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="card" style={{ maxWidth: 720 }}>
      <h2 style={{ marginTop: 0 }}>{initial ? 'Editar producto' : 'Nuevo producto'}</h2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '1.5rem' }}>
        <div>
          <div className="field">
            <label>SKU / Referencia</label>
            <input className="input" value={sku} onChange={(e) => setSku(e.target.value)} required />
          </div>
          <div className="field">
            <label>Nombre</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field">
            <label>Descripción</label>
            <textarea
              className="input"
              rows={3}
              value={description ?? ''}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
            <div className="field">
              <label>Precio compra</label>
              <input
                className="input"
                type="number"
                step="0.01"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>IVA (%)</label>
              <input
                className="input"
                type="number"
                step="0.01"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Precio venta</label>
              <input
                className="input"
                type="number"
                step="0.01"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                required
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={isService} onChange={(e) => setIsService(e.target.checked)} />
              Servicio (sin stock)
            </label>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={isFavorite} onChange={(e) => setIsFavorite(e.target.checked)} />
              Favorito en POS
            </label>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Activo
            </label>
          </div>
        </div>

        <div>
          <label style={{ fontSize: '0.875rem', color: 'var(--color-muted)' }}>Imagen</label>
          <div
            style={{
              width: '100%',
              aspectRatio: '1 / 1',
              border: '1px dashed var(--color-border)',
              borderRadius: 'var(--radius)',
              marginTop: '0.35rem',
              backgroundImage: preview ? `url(${preview})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'grid',
              placeItems: 'center',
              color: 'var(--color-muted)',
              fontSize: '0.85rem',
            }}
          >
            {!preview && 'Sin imagen'}
          </div>
          <input
            type="file"
            accept="image/*"
            style={{ marginTop: '0.5rem', width: '100%' }}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
